// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { ChevronDown, ChevronUp, ExternalLink, RefreshCw } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemId } from "@/components/ui/system-id";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import {
  type Opportunity,
  useOpportunityDetails,
  useOpportunityDirectionAnalysis,
  useRegenerateDirectionAnalysis,
} from "@/hooks/api/use-opportunities";
import { formatDate } from "@/lib/date-formatting";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";

// A simple collapsible toggle component (moved here)
interface CollapsibleToggleProps {
  initialState?: boolean;
  renderContent: (isOpen: boolean, toggle: () => void) => ReactNode;
}

function CollapsibleToggle({
  initialState = false,
  renderContent,
}: CollapsibleToggleProps) {
  const [isOpen, setIsOpen] = useState(initialState);
  const toggle = () => setIsOpen(!isOpen);
  return <>{renderContent(isOpen, toggle)}</>;
}

// Interface for the main component (moved here)
interface OpportunityDetailsSheetProps {
  opportunity: Opportunity | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Main component (moved here and exported)
export function OpportunityDetailsSheet({
  opportunity,
  isOpen,
  onOpenChange,
}: OpportunityDetailsSheetProps) {
  const { data: connections } = useExternalConnectionLinks();
  const { data: opportunityDetails, isLoading } = useOpportunityDetails({
    opportunityId: opportunity?.id,
    accountId: opportunity?.account_id,
    enabled: isOpen,
  });
  const { data: directionAnalysis, isLoading: isLoadingDirection } =
    useOpportunityDirectionAnalysis({
      opportunityId: opportunity?.id,
      enabled: isOpen,
    });

  const regenerateDirectionMutation = useRegenerateDirectionAnalysis();

  const handleRegenerateDirection = () => {
    if (!opportunity?.id || !directionAnalysis?.taskId) return;
    regenerateDirectionMutation.mutate({
      taskId: directionAnalysis.taskId,
      opportunityId: opportunity.id,
    });
  };

  if (!isOpen) return null;

  const renderLoadingState = () => (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2 mb-2">
        <Skeleton className="h-10 w-full sm:w-1/2" />
        <Skeleton className="h-10 w-full sm:w-1/2" />
      </div>
      <div className="bg-muted/30 p-4 rounded-lg grid grid-cols-2 gap-x-4 gap-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-1 text-sm col-span-1">
            <Skeleton className="h-4 w-[4.5rem]" />
            <Skeleton className="h-4 w-[90px]" />
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="bg-muted/30 p-4 rounded-lg flex-1 flex flex-col items-center justify-center">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="bg-muted/30 p-4 rounded-lg flex-1 flex flex-col items-center justify-center">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
      <div className="bg-muted/30 p-4 rounded-lg flex flex-row gap-6 justify-center">
        <div className="flex flex-col items-center">
          <Skeleton className="h-4 w-16 mb-2" />
          <Skeleton className="h-8 w-8" />
        </div>
        <div className="flex flex-col items-center">
          <Skeleton className="h-4 w-16 mb-2" />
          <Skeleton className="h-8 w-8" />
        </div>
      </div>
      <div className="bg-muted/30 p-4 rounded-lg">
        <Skeleton className="h-4 w-24 mb-2" />
        <Skeleton className="h-8 w-24" />
      </div>
    </div>
  );

  const renderOpportunityDetails = () => {
    if (
      !opportunity ||
      !opportunityDetails ||
      isLoading ||
      isLoadingDirection
    ) {
      return renderLoadingState();
    }
    const crmUrl = getExternalConnectionLink(opportunity, connections);
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2 pb-4 mb-4 border-b">
          <Button
            variant="outline"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                `/accounts/${opportunity.account_id}/explorer?opportunityId=${opportunity.id}`,
                "_blank",
              );
            }}
          >
            Open in Story Explorer
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                `/accounts/${opportunity.account_id}/milestone-analyses?opportunityId=${opportunity.id}`,
                "_blank",
              );
            }}
          >
            Open Milestone Analysis
          </Button>
        </div>

        <div className="space-y-1 text-sm text-muted-foreground">
          <div className="flex justify-between items-center">
            <span className="font-medium">Type:</span>
            <span>{opportunity.type || "N/A"}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium">Created:</span>
            <span>
              {opportunity.created_date
                ? formatDate(opportunity.created_date as string)
                : "N/A"}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium">Close Date:</span>
            <span>
              {opportunity.close_date
                ? formatDate(opportunity.close_date as string)
                : "N/A"}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium">Source ID:</span>
            {crmUrl ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={crmUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:bg-muted hover:text-foreground py-1 px-2 rounded-md transition-colors -mr-2 flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>{opportunity.source_record_id}</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    View in{" "}
                    {getExternalSystemName(opportunity.source_record_type)}
                  </p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span>{opportunity.source_record_id || "N/A"}</span>
            )}
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium">Account:</span>
            <span>{opportunity.account_name || "N/A"}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="bg-muted/30 p-4 rounded-lg flex-1 flex flex-col items-center justify-center min-w-[160px]">
            <div className="text-sm text-muted-foreground">
              Opportunity Amount
            </div>
            <div className="text-3xl font-bold">
              {opportunity.total_amount?.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }) ?? "N/A"}
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg flex-1 flex flex-col items-center justify-center min-w-[120px]">
            <div className="text-sm text-muted-foreground">Stage</div>
            <div className="text-xl font-bold">
              {opportunity.stage_name || "N/A"}
            </div>
          </div>
        </div>

        <div className="bg-muted/30 p-4 rounded-lg flex flex-row gap-6 justify-center">
          <div className="flex flex-col items-center flex-1 min-w-[100px]">
            <div className="text-xs text-muted-foreground mb-1">
              Contact Roles
            </div>
            <div className="text-2xl font-bold">
              {opportunityDetails.contacts?.filter((contact) =>
                contact.relation_sources.includes("crm_contact_role"),
              ).length || 0}
            </div>
            <div className="text-xs text-muted-foreground">Total</div>
          </div>
          <div className="border-l border-muted h-10 mx-4" />
          <div className="flex flex-col items-center flex-1 min-w-[100px]">
            <div className="text-xs text-muted-foreground mb-1">
              Buying Group Size
            </div>
            <div className="text-2xl font-bold">
              {opportunityDetails.contacts?.length || 0}
            </div>
            <div className="text-xs text-muted-foreground">Total Members</div>
          </div>
        </div>

        {directionAnalysis && (
          <div className="bg-muted/30 p-4 rounded-lg">
            <CollapsibleToggle
              initialState={false}
              renderContent={(isOpen, toggle) => (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <div className="text-xs text-muted-foreground">
                        Direction
                      </div>
                      <div className="text-lg font-bold">
                        {directionAnalysis?.value
                          ? directionAnalysis.value.charAt(0).toUpperCase() +
                            directionAnalysis.value.slice(1).toLowerCase()
                          : "Undetermined"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRegenerateDirection}
                            disabled={
                              regenerateDirectionMutation.isPending ||
                              !directionAnalysis?.taskId
                            }
                            className="h-7 px-2"
                          >
                            <RefreshCw
                              className={`h-3.5 w-3.5 ${
                                regenerateDirectionMutation.isPending
                                  ? "animate-spin"
                                  : ""
                              }`}
                            />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Regenerate direction analysis</p>
                        </TooltipContent>
                      </Tooltip>
                      {(directionAnalysis?.opinion ||
                        (directionAnalysis?.touchpointCitations &&
                          directionAnalysis.touchpointCitations.length > 0) ||
                        (directionAnalysis?.otherCitations &&
                          directionAnalysis.otherCitations.length > 0)) && (
                        <button
                          onClick={toggle}
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          {isOpen ? (
                            <>
                              Hide details <ChevronUp className="h-3 w-3" />
                            </>
                          ) : (
                            <>
                              Show details <ChevronDown className="h-3 w-3" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {isOpen &&
                    (directionAnalysis?.opinion ||
                      (directionAnalysis?.touchpointCitations &&
                        directionAnalysis.touchpointCitations.length > 0) ||
                      (directionAnalysis?.otherCitations &&
                        directionAnalysis.otherCitations.length > 0)) && (
                      <div className="space-y-4 pt-2 border-t border-muted mt-2">
                        {directionAnalysis?.opinion && (
                          <div>
                            <p className="text-sm mb-2">
                              {directionAnalysis.opinion.opinion || ""}
                            </p>
                          </div>
                        )}
                        {directionAnalysis?.touchpointCitations &&
                          directionAnalysis.touchpointCitations.length > 0 && (
                            <div>
                              <div className="text-xs font-medium mb-1">
                                Cited touchpoints (
                                {directionAnalysis.touchpointCitations.length})
                              </div>
                              <div className="space-y-2">
                                {directionAnalysis.touchpointCitations.map(
                                  (touchpoint, index) => (
                                    <div
                                      key={`tp-${index}`}
                                      className="border-b pb-2"
                                    >
                                      <div className="mb-1">
                                        <div className="flex items-baseline justify-between">
                                          <div>
                                            <span className="text-xs text-muted-foreground mr-2">
                                              Activity:
                                            </span>
                                            <span className="text-xs font-medium">
                                              {touchpoint.detail ||
                                                touchpoint.type}
                                            </span>
                                            {touchpoint.type && (
                                              <span className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded-full">
                                                {touchpoint.type}
                                              </span>
                                            )}
                                          </div>
                                          {touchpoint.date && (
                                            <span className="text-xs text-muted-foreground">
                                              {touchpoint.date}
                                            </span>
                                          )}
                                        </div>
                                        {touchpoint.reason && (
                                          <div className="mt-1">
                                            <span className="text-xs text-muted-foreground mr-2">
                                              Reason:
                                            </span>
                                            <span className="text-xs">
                                              {touchpoint.reason}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      {touchpoint.id && (
                                        <div className="flex justify-end">
                                          <SystemId
                                            className="flex justify-end"
                                            id={touchpoint.id}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        {directionAnalysis?.otherCitations &&
                          directionAnalysis.otherCitations.length > 0 && (
                            <div>
                              <div className="text-xs font-medium mb-1">
                                Other citations (
                                {directionAnalysis.otherCitations.length})
                              </div>
                              <div className="space-y-2">
                                {directionAnalysis.otherCitations.map(
                                  (citation, index) => (
                                    <div
                                      key={`oc-${index}`}
                                      className="border-b pb-2"
                                    >
                                      <div className="mb-1">
                                        {citation.id && (
                                          <div className="mb-1">
                                            <span className="text-xs text-muted-foreground mr-2">
                                              Entity:
                                            </span>
                                            <span className="text-xs bg-slate-100 px-2 py-1 rounded-full mr-1">
                                              {citation.field}
                                            </span>
                                            <span className="text-xs font-medium">
                                              {citation.id}
                                            </span>
                                          </div>
                                        )}
                                        {citation.detail && (
                                          <div className="mb-1">
                                            <span className="text-xs text-muted-foreground mr-2">
                                              Detail:
                                            </span>
                                            <span className="text-xs">
                                              {citation.detail}
                                            </span>
                                          </div>
                                        )}
                                        {citation.reason && (
                                          <div className="mb-1">
                                            <span className="text-xs text-muted-foreground mr-2">
                                              Reason:
                                            </span>
                                            <span className="text-xs">
                                              {citation.reason}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      {citation.id && (
                                        <div className="flex justify-end">
                                          <SystemId
                                            className="flex justify-end"
                                            id={citation.id}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                </>
              )}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl bg-card">
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-2xl">
              {opportunity ? opportunity.name : "Opportunity Overview"}
            </SheetTitle>
          </div>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-8rem)] mt-6">
          {renderOpportunityDetails()}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
